/**
 * The agent's resolved system prompt: snippets expanded, `{{variables}}` left in
 * place and highlighted, since those are what a dataset item fills. Long prompts
 * start clipped to a few lines with a toggle to read the rest.
 */

import { useState, type ReactNode } from "react";
import { Button } from "@/components/ui/button";

const COLLAPSED_LINES = 8;
const VARIABLE = /(\{\{\s*[^{}]+?\s*\}\})/g;

/** Split on `{{name}}` and wrap each variable in a mark. */
function highlight(text: string): ReactNode[] {
  return text.split(VARIABLE).map((part, i) =>
    i % 2 === 1 ? (
      <mark key={i} className="rounded-[3px] bg-run-bg px-0.5 text-link">
        {part}
      </mark>
    ) : (
      part
    ),
  );
}

export function InstructionsBlock({ text }: { text: string }) {
  const [open, setOpen] = useState(false);
  const lines = text.split("\n");
  const long = lines.length > COLLAPSED_LINES;
  const shown = open || !long ? text : lines.slice(0, COLLAPSED_LINES).join("\n");

  return (
    <div className="flex flex-col">
      <pre
        aria-label="System prompt"
        className="m-0 overflow-auto bg-well px-4 py-3.5 font-mono text-xs leading-relaxed
                   whitespace-pre-wrap text-code"
      >
        {highlight(shown)}
        {long && !open && <span className="text-dim">{"\n"}…</span>}
      </pre>
      {long && (
        <div className="border-t border-line-soft px-4 py-2">
          <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen(!open)}>
            {open ? "Show less" : `Show all ${lines.length} lines`}
          </Button>
        </div>
      )}
    </div>
  );
}
